import { css } from "@emotion/react";
import Stack from "@mui/material/Stack";
import { theme } from "@polifonia/theme";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/Badge";
import { MotifIcon } from "@/components/MotifIcon";
import { Typography } from "@/components/Typography";
import { Trans } from "@/features/i18n/Trans";
import type { InterludeTypes } from "@/interlude";

type ProductFeed = InterludeTypes["ProductFeed"];

const cssContainer = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  background-color: ${theme.colors.white};
  border-radius: 8px;
  border: 1px solid ${theme.colors.staticFgLine};
`;

const cssTitle = css`
  font-weight: 600;
  font-size: 18px;
  color: ${theme.colors.staticFgPrimary};
  margin-bottom: 8px;
`;

const cssSection = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const cssSectionTitle = css`
  font-weight: 600;
  color: ${theme.colors.staticFgPrimary};
`;

const cssMessageList = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-left: 8px;
`;

const cssMessageItem = css`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  color: ${theme.colors.staticFgPrimary};
`;

const cssStatusBadgeWrapper = css`
  display: flex;
  justify-content: flex-start;
  width: fit-content;
`;

// Import blocking errors - these prevent import from starting
const IMPORT_BLOCKING_ERRORS = [
  "TRIGGER_UPLOAD_JOB_FAILED",
  "FEED_URL_MISSING",
  "DOWNLOAD_FAILED",
  "FILE_ENCODING_CHECK_FAILED",
  "FILE_TYPE_CHECK_FAILED",
  "FILE_TYPE_DETECTION_FAILED",
  "NO_PRODUCTS_IN_FILE",
  "VALIDATION_PROCESS_FAILED",
  "GCS_UPLOAD_FAILED",
  "UPLOAD_PROCESS_FAILED",
  "CSV_FIELD_SIZE_LIMIT_EXCEEDED",
];

// Error code to React node mapping
const ERROR_CODE_TO_REACT_NODE: Record<
  string,
  (count: number) => React.ReactNode
> = {
  TRIGGER_UPLOAD_JOB_FAILED: (count) => (
    <Trans
      i18nKey="productFeed.feedCheckSummary.errors.triggerUploadJobFailed"
      values={{ count }}
    />
  ),
  FEED_URL_MISSING: (count) => (
    <Trans
      i18nKey="productFeed.feedCheckSummary.errors.feedUrlMissing"
      values={{ count }}
    />
  ),
  DOWNLOAD_FAILED: (count) => (
    <Trans
      i18nKey="productFeed.feedCheckSummary.errors.downloadFailed"
      values={{ count }}
    />
  ),
  FILE_ENCODING_CHECK_FAILED: (count) => (
    <Trans
      i18nKey="productFeed.feedCheckSummary.errors.fileEncodingCheckFailed"
      values={{ count }}
    />
  ),
  FILE_TYPE_CHECK_FAILED: (count) => (
    <Trans
      i18nKey="productFeed.feedCheckSummary.errors.fileTypeCheckFailed"
      values={{ count }}
    />
  ),
  FILE_TYPE_DETECTION_FAILED: (count) => (
    <Trans
      i18nKey="productFeed.feedCheckSummary.errors.fileTypeCheckFailed"
      values={{ count }}
    />
  ),
  NO_PRODUCTS_IN_FILE: (count) => (
    <Trans
      i18nKey="productFeed.feedCheckSummary.errors.noProductsInFile"
      values={{ count }}
    />
  ),
  VALIDATION_PROCESS_FAILED: (count) => (
    <Trans
      i18nKey="productFeed.feedCheckSummary.errors.validationProcessFailed"
      values={{ count }}
    />
  ),
  GCS_UPLOAD_FAILED: (count) => (
    <Trans
      i18nKey="productFeed.feedCheckSummary.errors.gcsUploadFailed"
      values={{ count }}
    />
  ),
  UPLOAD_PROCESS_FAILED: (count) => (
    <Trans
      i18nKey="productFeed.feedCheckSummary.errors.uploadProcessFailed"
      values={{ count }}
    />
  ),
  CSV_FIELD_SIZE_LIMIT_EXCEEDED: (count) => (
    <Trans
      i18nKey="productFeed.feedCheckSummary.errors.csvFieldSizeLimitExceeded"
      values={{ count }}
    />
  ),
  DUPLICATE_PRODUCT_ID: (count) => (
    <Trans
      i18nKey="productFeed.feedCheckSummary.errors.duplicateProductId"
      values={{ count }}
    />
  ),
  MISSING_REQUIRED_FIELD_PRODUCT_ID: (count) => (
    <Trans
      i18nKey="productFeed.feedCheckSummary.errors.missingRequiredFieldProductId"
      values={{ count }}
    />
  ),
  MISSING_REQUIRED_FIELD_TITLE: (count) => (
    <Trans
      i18nKey="productFeed.feedCheckSummary.errors.missingRequiredFieldTitle"
      values={{ count }}
    />
  ),
  MISSING_REQUIRED_FIELD_DESCRIPTION: (count) => (
    <Trans
      i18nKey="productFeed.feedCheckSummary.errors.missingRequiredFieldDescription"
      values={{ count }}
    />
  ),
  MISSING_REQUIRED_FIELD_LINK: (count) => (
    <Trans
      i18nKey="productFeed.feedCheckSummary.errors.missingRequiredFieldLink"
      values={{ count }}
    />
  ),
  MISSING_REQUIRED_FIELD_IMAGE_LINK: (count) => (
    <Trans
      i18nKey="productFeed.feedCheckSummary.errors.missingRequiredFieldImageLink"
      values={{ count }}
    />
  ),
  MISSING_REQUIRED_FIELD_PRICE: (count) => (
    <Trans
      i18nKey="productFeed.feedCheckSummary.errors.missingRequiredFieldPrice"
      values={{ count }}
    />
  ),
};

// Warning code to React node mapping
const WARNING_CODE_TO_REACT_NODE: Record<
  string,
  (count: number) => React.ReactNode
> = {
  MISSING_BRAND: (count) => (
    <Trans
      i18nKey="productFeed.feedCheckSummary.warning.missingBrand"
      values={{ count }}
    />
  ),
  MISSING_CATEGORY: (count) => (
    <Trans
      i18nKey="productFeed.feedCheckSummary.warning.missingCategory"
      values={{ count }}
    />
  ),
  MISSING_AVAILABILITY: (count) => (
    <Trans
      i18nKey="productFeed.feedCheckSummary.warning.missingAvailability"
      values={{ count }}
    />
  ),
};

const parseCountValue = (count: unknown): number => {
  return typeof count === "number" ? count : Number(count) || 0;
};

interface FeedCheckSummaryProps {
  feed: ProductFeed;
}

export const FeedCheckSummary: React.FC<FeedCheckSummaryProps> =
  function FeedCheckSummary({ feed }) {
    const { t } = useTranslation();
    const { processingResult } = feed;

    const {
      errors = {},
      warnings = {},
      uploadedRows = 0,
    } = processingResult || {};
    const hasErrors = Object.keys(errors).length > 0;
    const hasWarnings = Object.keys(warnings).length > 0;

    // Separate errors into import blocking and required fixes
    const { importBlockingErrors, requiredFixesErrors } = useMemo(() => {
      const importBlocking: Record<string, number> = {};
      const requiredFixes: Record<string, number> = {};

      Object.entries(errors).forEach(([errorCode, count]) => {
        if (IMPORT_BLOCKING_ERRORS.includes(errorCode)) {
          importBlocking[errorCode] = count;
        } else {
          requiredFixes[errorCode] = count;
        }
      });

      return {
        importBlockingErrors: importBlocking,
        requiredFixesErrors: requiredFixes,
      };
    }, [errors]);

    const hasImportBlockingErrors =
      Object.keys(importBlockingErrors).length > 0;
    const hasRequiredFixesErrors = Object.keys(requiredFixesErrors).length > 0;

    // Render status box (top section) - always displayed as Badge
    const statusBox: React.ReactNode = useMemo(() => {
      // Case 1: uploadedRows === 0 -> upload failed
      if (uploadedRows === 0) {
        return (
          <div css={cssStatusBadgeWrapper}>
            <Badge variant="negative">
              <Stack
                direction="row"
                spacing="4px"
                sx={{ alignItems: "center" }}
              >
                <MotifIcon un-i-motif="circle_cross" />
                <span>
                  {t("productFeed.feedCheckSummary.statusBox.uploadFailed")}
                </span>
              </Stack>
            </Badge>
          </div>
        );
      }

      // Case 2: has errors or warnings -> show uploaded count
      if (hasErrors || hasWarnings) {
        return (
          <div css={cssStatusBadgeWrapper}>
            <Badge variant="attentive">
              <Stack
                direction="row"
                spacing="4px"
                sx={{ alignItems: "center" }}
              >
                <MotifIcon un-i-motif="circle_check" />
                <span>
                  {t("productFeed.feedCheckSummary.statusBox.uploaded", {
                    count: uploadedRows,
                  })}
                </span>
              </Stack>
            </Badge>
          </div>
        );
      }

      // Case 3: else (no errors/warnings) -> show success message
      return (
        <div css={cssStatusBadgeWrapper}>
          <Badge variant="superior">
            <Stack direction="row" spacing="4px" sx={{ alignItems: "center" }}>
              <MotifIcon un-i-motif="circle_check" />
              <span>
                {t("productFeed.feedCheckSummary.successMessage.allGood")}
              </span>
            </Stack>
          </Badge>
        </div>
      );
    }, [hasErrors, hasWarnings, t, uploadedRows]);

    const importBlockingErrorsEntries = useMemo(
      () =>
        Object.entries(importBlockingErrors).map(
          ([errorCode, count]) =>
            [errorCode, parseCountValue(count)] as [string, number],
        ),
      [importBlockingErrors],
    );

    const importBlockingErrorsNode: React.ReactNode = useMemo(() => {
      if (!hasImportBlockingErrors) {
        return null;
      }

      return (
        <div css={cssSection}>
          <Typography variant="body" css={cssSectionTitle}>
            {t("productFeed.feedCheckSummary.title.importFailed")}
          </Typography>
          <div css={cssMessageList}>
            {importBlockingErrorsEntries.map(([errorCode, countValue]) => {
              const getReactNode =
                ERROR_CODE_TO_REACT_NODE[errorCode] ||
                ((count: number) => (
                  <Trans
                    i18nKey={`productFeed.feedCheckSummary.errors.${errorCode}`}
                    values={{ count }}
                  />
                ));

              return (
                <div key={errorCode} css={cssMessageItem}>
                  <MotifIcon un-i-motif="cross" />
                  <Typography variant="body">
                    {getReactNode(countValue)}
                  </Typography>
                </div>
              );
            })}
          </div>
        </div>
      );
    }, [hasImportBlockingErrors, importBlockingErrorsEntries, t]);

    const requiredFixesErrorsEntries = useMemo(
      () =>
        Object.entries(requiredFixesErrors).map(
          ([errorCode, count]) =>
            [errorCode, parseCountValue(count)] as [string, number],
        ),
      [requiredFixesErrors],
    );

    const requiredFixesErrorsNode: React.ReactNode = useMemo(() => {
      if (!hasRequiredFixesErrors) {
        return null;
      }

      return (
        <div css={cssSection}>
          <Typography variant="body" css={cssSectionTitle}>
            {t("productFeed.feedCheckSummary.title.requiredFixes")}
          </Typography>
          <div css={cssMessageList}>
            {requiredFixesErrorsEntries.map(([errorCode, countValue]) => {
              const getReactNode =
                ERROR_CODE_TO_REACT_NODE[errorCode] ||
                ((count: number) => (
                  <Trans
                    i18nKey={`productFeed.feedCheckSummary.errors.${errorCode}`}
                    values={{ count }}
                  />
                ));

              return (
                <div key={errorCode} css={cssMessageItem}>
                  <MotifIcon un-i-motif="cross" />
                  <Typography variant="body">
                    {getReactNode(countValue)}
                  </Typography>
                </div>
              );
            })}
          </div>
        </div>
      );
    }, [hasRequiredFixesErrors, requiredFixesErrorsEntries, t]);

    const warningsEntries = useMemo(
      () =>
        Object.entries(warnings).map(
          ([warningCode, count]) =>
            [warningCode, parseCountValue(count)] as [string, number],
        ),
      [warnings],
    );

    const warningsNode: React.ReactNode = useMemo(() => {
      if (!hasWarnings) {
        return null;
      }

      return (
        <div css={cssSection}>
          <Typography variant="body" css={cssSectionTitle}>
            {t("productFeed.feedCheckSummary.title.qualityWarnings")}
          </Typography>
          <div css={cssMessageList}>
            {warningsEntries.map(([warningCode, countValue]) => {
              const getReactNode =
                WARNING_CODE_TO_REACT_NODE[warningCode] ||
                ((count: number) => (
                  <Trans
                    i18nKey={`productFeed.feedCheckSummary.warning.${warningCode}`}
                    values={{ count }}
                  />
                ));

              return (
                <div key={warningCode} css={cssMessageItem}>
                  <MotifIcon un-i-motif="warning" />
                  <Typography variant="body">
                    {getReactNode(countValue)}
                  </Typography>
                </div>
              );
            })}
          </div>
        </div>
      );
    }, [hasWarnings, warningsEntries, t]);

    if (!processingResult) {
      return null;
    }

    return (
      <div css={cssContainer}>
        <Typography variant="h3" css={cssTitle}>
          {t("productFeed.feedCheckSummary.title")}
        </Typography>

        {statusBox}
        {importBlockingErrorsNode}
        {requiredFixesErrorsNode}
        {warningsNode}
      </div>
    );
  };
